/**
 * Ortak tablo gösterim:
 * - 2+ kelimeli header → kısa label + hover title
 * - uzun hücre → sütun genişliğine göre satır clamp + "devamını oku"
 * - iş emri/teklif no + tarihler dar; personel/makine/uyf/problem geniş
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import { columnLabel, currentLang, englishHeaderShort, statusLabel } from "../i18n/messages";

const HEADER_ABBR = {
  "Başlangıç Tarihi": "Baş. Tar.",
  "Bitiş Tarihi": "Bit. Tar.",
  "İletilen Problemler": "İlet. Prob.",
  "Günlük Rapor": "Gün.",
  "Ziyaretçi Raporu": "Ziy.",
  "Durum Güncelle": "Dur. Gün.",
  "İş Emri No": "İş Em. No",
  "UYF No": "UYF",
  "Seri No": "Seri",
  "Teklif No": "Tek. No",
  "Teklif Tarihi": "Tek. Tar.",
  "Onay Tarihi": "Onay Tar.",
  "Sevk Tarihi": "Sevk Tar.",
  "Ödeme Tarihi": "Öd. Tar.",
  "Rapor Tarihi": "Rap. Tar.",
  "Müşteri Adı": "Müşteri",
  "Lokasyon Adı": "Lokasyon",
  "Makine Adı": "Makine",
  "Personel Adı": "Personel",
  "Kayıt Tarihi": "Kay. Tar.",
  "Son Güncelleme": "Son Gün.",
  "Pasif Devam": "Pas. Dev.",
};

const LONG_TEXT_NAMES = new Set([
  "makineler",
  "makine",
  "iletilen_problemler",
  "iletilen_problem",
  "aciklama",
  "not",
  "notlar",
  "adres",
  "lokasyon_adresi",
  "sebep",
  "servis_sebebi",
  "uyfler",
  "personeller",
  "teknik_personeller",
  "firma",
  "musteri",
]);

function wordCount(s) {
  return String(s || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

export function abbreviateHeader(label) {
  const full = String(label || "").trim();
  if (!full) return full;
  if (HEADER_ABBR[full]) return HEADER_ABBR[full];
  const words = full.split(/\s+/).filter(Boolean);
  if (words.length < 2) return full;
  return words
    .map((w) => {
      if (w.length <= 4) return w;
      if (/^(no|no\.|id)$/i.test(w)) return w;
      return `${w.slice(0, 3)}.`;
    })
    .join(" ");
}

/** Sütun genişliğini doldurur; taşınca satır clamp + devamını oku.
 *  Kısa metinde (minChars altı) hiç kısaltılmaz.
 */
export function ClampText({ text, maxLines = 5, minChars = 180 }) {
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef(null);
  const raw = text == null ? "" : String(text);
  const allowClamp = raw.trim().length >= minChars;

  useLayoutEffect(() => {
    if (!raw.trim() || open || !allowClamp) {
      setOverflows(false);
      return undefined;
    }
    const el = textRef.current;
    if (!el) return undefined;
    const check = () => {
      setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [raw, open, maxLines, allowClamp]);

  if (!raw.trim()) return null;

  if (!allowClamp) {
    return (
      <span className="ct-clamp">
        <span className="ct-clamp-text is-open">{raw}</span>
      </span>
    );
  }

  return (
    <span className="ct-clamp">
      <span
        ref={textRef}
        className={open ? "ct-clamp-text is-open" : "ct-clamp-text"}
        style={open ? undefined : { WebkitLineClamp: maxLines }}
      >
        {raw}
      </span>
      {(overflows || open) && (
        <button
          type="button"
          className="ct-cell-more"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          {open ? "kısalt" : "devamını oku"}
        </button>
      )}
    </span>
  );
}

function shouldClampColumn(col) {
  const name = String(col.name || "").toLowerCase();
  const label = String(col.label || "").toLowerCase();
  if (LONG_TEXT_NAMES.has(name)) return true;
  if (/makine|problem|açıklama|aciklama|adres|not/.test(name)) return true;
  if (/makine|problem|açıklama|adres/.test(label)) return true;
  return false;
}

function colKind(col) {
  const name = String(col.name || "").toLowerCase();
  const label = String(col.label || "").toLowerCase();
  // Günlük/Ziyaret rapor: name bazen is_emri_no — label önce
  if (
    /gunluk_rapor|ziyaret_rapor/.test(name) ||
    /günlük rapor|ziyaretçi rapor|daily report|visit report/.test(label)
  ) {
    return "rapor";
  }
  // No / teklif no: içerik kısa ama tablo şişirmesin
  if (
    /is_emri_no|teklif_no/.test(name) ||
    /iş emri no|teklif no/.test(label)
  ) {
    return "id";
  }
  // MSN (10 hane) / Tip ("M. Memnuniyet") — tiny (52px) üstüne biner
  if (/^msn$/.test(name) || /^msn$/.test(label)) return "msn";
  if (/^tip$/.test(name) || /^tip$/.test(label)) return "tip";
  if (/^durum$/.test(name) || /^durum$/.test(label)) return "durum";
  if (/makine/.test(name) || /^makine/.test(label)) return "makine";
  if (/lokasyon/.test(name) || /lokasyon/.test(label)) return "lokasyon";
  if (/iletilen_problem|problem/.test(name) || /ilet\.?\s*prob|iletilen/.test(label)) {
    return "problem";
  }
  // UYF kısa; Seri biraz daha geniş (5–6 hane)
  if (/uyf/.test(name) || /^uyf/.test(label)) return "uyf";
  if (/^seri|seri_no|seri_nolar/.test(name) || /^seri/.test(label)) return "seri";
  if (/personel|teknik_personel/.test(name) || /personel/.test(label)) return "personel";
  if (/temsilci/.test(name) || /temsilci/.test(label)) return "temsilci";
  if (/tarih|_date$/.test(name) || /\btar\./.test(label)) return "date";
  // Müşteri — kısa isimler; yüzde ile şişmesin
  if (
    /^firma$/.test(name) ||
    /musteri|müşteri/.test(name) ||
    /müşteri|musteri/.test(label)
  ) {
    return "musteri";
  }
  if (/^kw$|kilowatt/.test(name) || /^kw$/.test(label)) return "tiny";
  return "default";
}

function mergeProps(prevFn, extra) {
  return (...args) => {
    const prev = typeof prevFn === "function" ? prevFn(...args) || {} : {};
    const className = [prev.className, extra.className].filter(Boolean).join(" ");
    return {
      ...prev,
      ...extra,
      className: className || undefined,
      style: { ...(prev.style || {}), ...(extra.style || {}) },
      title: extra.title != null ? extra.title : prev.title,
    };
  };
}

function peelMachineName(token) {
  const parts = String(token || "").split("*");
  if (parts.length < 3) return String(token || "").trim();
  let index = 0;
  let uyf = "";
  let seri = "";
  while (index + 2 < parts.length) {
    const head = parts[index];
    const mid = parts[index + 1];
    if (!head || !mid || /\s/.test(head) || /\s/.test(mid)) break;
    uyf = head;
    seri = mid;
    index += 2;
    if (parts[index] === uyf && parts[index + 1] === seri) continue;
    break;
  }
  return parts.slice(index).join("*").trim();
}

/** Makine adı. UYF ve seri bu kolona karışmaz, adın içindeki boşluk bölünmez. */
export function machineNameList(value) {
  if (value == null || value === "" || value === "null") return [];
  let tokens = [];
  if (Array.isArray(value)) {
    tokens = value.map((item) => String(item));
  } else {
    const raw = String(value).trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      tokens = Array.isArray(parsed) ? parsed.map((item) => String(item)) : [raw];
    } catch (err) {
      tokens = raw.split(",").map((item) => item.trim()).filter(Boolean);
    }
  }
  return tokens.map(peelMachineName).filter(Boolean);
}

export function codeList(value) {
  if (value == null || value === "" || value === "null") return [];
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  const raw = String(value).trim();
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter(Boolean);
    }
  } catch (err) {
    /* düz metin */
  }
  return raw.split(/[,;\s]+/).map((item) => item.trim()).filter(Boolean);
}

/** UYF / seri: her numara kendi satırında. */
export function renderCodeStack(value) {
  const items = codeList(value);
  if (!items.length) return null;
  return (
    <span className="ct-code-stack">
      {items.map((item, index) => (
        <span key={`${item}-${index}`} className="ct-code-stack__item">
          {item}
        </span>
      ))}
    </span>
  );
}

/** İş emri no; teklif no varsa hemen altında. */
export function renderIsEmriNo(isEmriNo, teklifNo) {
  const no = isEmriNo == null ? "" : String(isEmriNo);
  const teklif = teklifNo == null ? "" : String(teklifNo).trim();
  if (!no && !teklif) return null;
  if (!teklif) return no;
  return (
    <span className="ct-code-stack">
      <span className="ct-code-stack__item">{no}</span>
      <span className="ct-code-stack__item ct-code-stack__teklif" title="Teklif no">
        {teklif}
      </span>
    </span>
  );
}

function wrapBodyRender(col) {
  if (!shouldClampColumn(col)) return col.options && col.options.customBodyRender;
  const prev = col.options && col.options.customBodyRender;
  return (value, tableMeta, updateValue) => {
    let content = value;
    try {
      content = prev ? prev(value, tableMeta, updateValue) : value;
    } catch (err) {
      content = codeList(value).join(", ") || value;
    }
    if (content == null || content === false) return content;
    if (React.isValidElement(content)) return content;
    return <ClampText text={content} maxLines={5} minChars={180} />;
  };
}

// Genişlik serbest — table-layout:auto; hücre içeriğine göre büyüsün.
// Sadece white-space / class (clamp vb.); min/max/width yok.
const WIDTH = {
  id: { className: "ct-col-id", style: { whiteSpace: "nowrap" } },
  tiny: { className: "ct-col-tiny", style: { whiteSpace: "nowrap" } },
  msn: { className: "ct-col-msn", style: { whiteSpace: "nowrap" } },
  tip: { className: "ct-col-tip", style: { whiteSpace: "nowrap" } },
  durum: { className: "ct-col-durum", style: { whiteSpace: "nowrap" } },
  makine: { className: "ct-col-makine", style: { whiteSpace: "normal" } },
  musteri: { className: "ct-col-musteri", style: { whiteSpace: "nowrap" } },
  lokasyon: { className: "ct-col-lokasyon", style: { whiteSpace: "nowrap" } },
  problem: { className: "ct-col-problem", style: { whiteSpace: "normal" } },
  rapor: { className: "ct-col-rapor", style: { whiteSpace: "nowrap" } },
  uyf: { className: "ct-col-uyf", style: { whiteSpace: "nowrap" } },
  seri: { className: "ct-col-seri", style: { whiteSpace: "nowrap" } },
  personel: { className: "ct-col-personel", style: { whiteSpace: "nowrap" } },
  temsilci: { className: "ct-col-temsilci", style: { whiteSpace: "nowrap" } },
  date: { className: "ct-col-date", style: { whiteSpace: "nowrap" } },
  narrow: { className: "ct-col-narrow", style: { whiteSpace: "nowrap" } },
  medium: { className: "ct-col-medium", style: { whiteSpace: "nowrap" } },
};

export function enhanceColumns(columns) {
  if (!Array.isArray(columns)) return columns;
  const english = currentLang() === "en";
  return columns.map((col) => {
    const options = { ...(col.options || {}) };
    const translated = columnLabel(String(col.label || "").trim());
    const fullLabel = String(translated || col.name || "").trim();
    let label = translated || col.label;
    let fullLabelKeep = null;

    if (english && col.name === "durum" && !options.customBodyRender) {
      options.customBodyRender = (value) => statusLabel(value);
    }

    // Görünen kısa; hover açıklaması her zaman orijinal tam başlık
    if (wordCount(fullLabel) >= 2) {
      const short = english ? (englishHeaderShort(fullLabel) || abbreviateHeader(fullLabel)) : abbreviateHeader(fullLabel);
      if (short !== fullLabel) {
        label = short;
        fullLabelKeep = fullLabel;
      }
    }

    const kind = colKind({ ...col, label: fullLabel });
    const w = WIDTH[kind];
    if (w || fullLabelKeep) {
      options.setCellHeaderProps = mergeProps(options.setCellHeaderProps, {
        ...(w || {}),
        ...(fullLabelKeep ? { title: fullLabelKeep } : {}),
      });
      if (w) {
        options.setCellProps = mergeProps(options.setCellProps, w);
      }
    }

    if (kind === "uyf" || kind === "seri") {
      options.customBodyRender = (value) => renderCodeStack(value);
    } else {
      const body = wrapBodyRender(col);
      if (body) options.customBodyRender = body;
    }

    return {
      ...col,
      label,
      ...(fullLabelKeep ? { fullLabel: fullLabelKeep } : {}),
      options,
    };
  });
}

/** Sort hover'da kısaltma değil tam başlık (column.fullLabel). */
export function withTableOptions(options = {}) {
  const prevBody = (options.textLabels && options.textLabels.body) || {};
  const prevTooltip = prevBody.columnHeaderTooltip;
  return {
    ...options,
    textLabels: {
      ...(options.textLabels || {}),
      body: {
        ...prevBody,
        columnHeaderTooltip: (column) => {
          if (column && column.fullLabel) return column.fullLabel;
          if (typeof prevTooltip === "function") return prevTooltip(column);
          return (column && column.label) || "";
        },
      },
    },
  };
}
